import type { NormalizedTranscriptEntry } from "./sessionAdapter";

/**
 * Factory for an in-memory, per-session transcript buffer.
 *
 * Most providers run the conversation server-side and expose a read endpoint,
 * so their transcript is fetched back at end of session. Kaltura and D-ID do
 * not: Kaltura's SDK is a puppet (we produce both sides of the conversation),
 * and D-ID's API has no endpoint that returns a single chat. For both, the
 * turns only exist in the running page, so they are accumulated here as they
 * happen and read back by the provider's session adapter in the shared
 * normalized shape.
 *
 * Each buffer lives for the lifetime of the page, which is the lifetime of a
 * session, and is keyed by session id so a restart cannot inherit stale turns.
 */
export function createLocalTranscriptStore() {
  const buffers = new Map<string, NormalizedTranscriptEntry[]>();
  const startedAt = new Map<string, number>();

  return {
    startTranscript(sessionId: string) {
      buffers.set(sessionId, []);
      startedAt.set(sessionId, Date.now());
    },
    appendTranscriptTurn(sessionId: string, role: "user" | "avatar", text: string) {
      const trimmed = text.trim();
      if (!trimmed) return;
      const rows = buffers.get(sessionId);
      if (!rows) return;
      const now = Date.now();
      const t0 = startedAt.get(sessionId) ?? now;
      rows.push({
        role,
        transcript: trimmed,
        absolute_timestamp: Math.round(now / 1000),
        relative_timestamp: Math.round((now - t0) / 1000),
      });
    },
    readTranscript(sessionId: string): NormalizedTranscriptEntry[] {
      return [...(buffers.get(sessionId) ?? [])];
    },
    clearTranscript(sessionId: string) {
      buffers.delete(sessionId);
      startedAt.delete(sessionId);
    },
  };
}
